/**
 * Landing page for Cognito's OAuth redirect after a social sign-in.
 *
 * Amplify intercepts the `?code=...&state=...` query and exchanges it
 * for tokens automatically. We watch the auth store and route the user
 * onward as soon as the tokens land (or bounce them home on failure).
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { paths } from '@/config/paths';

import { useAuth } from '../hooks/use-auth';

import styles from './oauth-callback.module.css';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate(paths.app, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    // If we sit on this screen for >10s without auth completing, treat
    // the redirect as failed and send the user back to start.
    const timeout = setTimeout(() => {
      if (!isAuthenticated) {
        setHasError(true);
      }
    }, 10_000);
    return () => clearTimeout(timeout);
  }, [isAuthenticated]);

  if (hasError) {
    return <Navigate to={`${paths.home}?error=oauth_failed`} replace />;
  }

  return (
    <main className={styles.wrap} aria-live="polite">
      <div className={styles.orb} />
      <h1 className={styles.title}>Wrapping up…</h1>
      <p className={styles.sub}>Hold tight, we&apos;re signing you in.</p>
    </main>
  );
}

export default OAuthCallbackPage;
